import { useEffect, useMemo, useState } from "react";

import { fetchPaymentMethods, fetchWallets } from "./paymentsService";
import { mapPaymentMethods, mapWallet } from "./paymentsMappers";
import { FALLBACK_CURRENCY } from "./money";

const walletRows = (data) => {
  if (Array.isArray(data)) return data;

  return data?.wallets || data?.content || [];
};

const COD_ROW = {
  code: "COD",
  title: "Cash on Delivery",
  description: "Pay in cash when your order is delivered.",
  enabled: true,
  balance: null,
  disabledReason: null,
};

const WALLET_ROW = {
  code: "WALLET",
  title: "Wallet",
  description: "Pay from your Sayagie wallet.",
  enabled: true,
  balance: null,
  disabledReason: null,
};

const usePaymentMethods = ({ currency } = {}) => {
  const [apiMethods, setApiMethods] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState("COD");

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchPaymentMethods(), fetchWallets().catch(() => null)]).then(
      ([methods, walletData]) => {
        if (cancelled) return;

        setApiMethods(mapPaymentMethods(methods));
        setWallets(walletRows(walletData).map(mapWallet).filter(Boolean));
        setIsLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const methods = useMemo(() => {
    const code = (currency || FALLBACK_CURRENCY).toUpperCase();
    const balance = wallets.find((row) => row.currencyCode === code)?.balance || {
      value: 0,
      currency: code,
    };

    const rows = apiMethods.map((row) =>
      row.code === "WALLET" && !row.balance ? { ...row, balance } : row
    );

    const hasCode = (value) => rows.some((row) => row.code === value);

    if (!hasCode("COD")) rows.unshift(COD_ROW);
    if (!hasCode("WALLET")) rows.push({ ...WALLET_ROW, balance });

    return rows;
  }, [apiMethods, wallets, currency]);

  const selectedMethod = methods.find((row) => row.code === selectedCode) || null;

  return {
    methods,
    wallets,
    isLoading,
    selectedCode,
    selectedMethod,
    selectMethod: setSelectedCode,
  };
};

export default usePaymentMethods;
